// src/utils/paymentFingerprint.js
// ============================================================
// PAYMENT FINGERPRINT & EXPECTED PAY DATE
// Analyzes payment history to determine when a tenant
// typically pays and predicts their next payment date.
// ============================================================

/**
 * Calculate a tenant's payment fingerprint
 * @param {Array} paymentHistory - Array of { date, amount, daysLate }
 * @returns {Object} - { averagePayDay, medianPayDay, consistency, expectedNextPayDate }
 */
export function calculatePaymentFingerprint(paymentHistory) {
  if (!paymentHistory || paymentHistory.length < 2) {
    return {
      averagePayDay: null,
      medianPayDay: null,
      consistency: 'insufficient_data',
      expectedNextPayDate: null,
      payCycle: 'unknown',
    };
  }

  // Extract the day-of-month each payment was made
  const payDays = paymentHistory
    .filter(p => p.amount > 0 && p.date)
    .map(p => {
      const date = new Date(p.date);
      return date.getDate(); // Day of month (1-31)
    });

  if (payDays.length < 2) {
    return { averagePayDay: null, medianPayDay: null, consistency: 'insufficient_data', expectedNextPayDate: null, payCycle: 'unknown' };
  }

  // Calculate average pay day
  const sum = payDays.reduce((a, b) => a + b, 0);
  const averagePayDay = Math.round(sum / payDays.length);

  // Calculate median pay day
  const sorted = [...payDays].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const medianPayDay = sorted.length % 2 !== 0
    ? sorted[mid]
    : Math.round((sorted[mid - 1] + sorted[mid]) / 2);

  // Calculate consistency (standard deviation of pay days)
  const variance = payDays.reduce((acc, day) => acc + Math.pow(day - averagePayDay, 2), 0) / payDays.length;
  const stdDev = Math.sqrt(variance);

  let consistency;
  if (stdDev <= 2) consistency = 'very_consistent';      // Always pays within 2 days of average
  else if (stdDev <= 5) consistency = 'consistent';       // Within 5 days
  else if (stdDev <= 10) consistency = 'variable';        // Varies significantly
  else consistency = 'unpredictable';                      // All over the place

  // Detect pay cycle (monthly vs biweekly)
  const payCycle = detectPayCycle(paymentHistory);

  // Calculate expected next pay date
  const expectedNextPayDate = calculateExpectedPayDate(medianPayDay);

  return {
    averagePayDay,
    medianPayDay,
    consistency,
    stdDev: Math.round(stdDev * 10) / 10,
    expectedNextPayDate,
    payCycle,
  };
}


/**
 * Detect if tenant pays monthly or biweekly
 * Biweekly payers often split rent into two payments
 * @param {Array} paymentHistory
 * @returns {string} - 'monthly', 'biweekly', or 'irregular'
 */
function detectPayCycle(paymentHistory) {
  if (!paymentHistory || paymentHistory.length < 4) return 'unknown';

  // Count payments per month
  const monthCounts = {};
  paymentHistory.forEach(p => {
    if (!p.date) return;
    const date = new Date(p.date);
    const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
    monthCounts[monthKey] = (monthCounts[monthKey] || 0) + 1;
  });

  const counts = Object.values(monthCounts);
  const avgPaymentsPerMonth = counts.reduce((a, b) => a + b, 0) / counts.length;

  if (avgPaymentsPerMonth >= 1.8) return 'biweekly';  // ~2 payments per month
  if (avgPaymentsPerMonth >= 0.8) return 'monthly';   // ~1 payment per month
  return 'irregular';
}


/**
 * Calculate expected next pay date based on median pay day
 * @param {number} medianPayDay - Typical day of month tenant pays
 * @returns {string} - ISO date string of expected next payment
 */
function calculateExpectedPayDate(medianPayDay) {
  if (!medianPayDay) return null;

  const now = new Date();
  const currentDay = now.getDate();
  
  let expectedDate;
  if (currentDay <= medianPayDay) {
    // Expected this month
    expectedDate = new Date(now.getFullYear(), now.getMonth(), medianPayDay);
  } else {
    // Expected next month
    expectedDate = new Date(now.getFullYear(), now.getMonth() + 1, medianPayDay);
  }

  return expectedDate.toISOString().split('T')[0];
}
